import { cn } from '@/lib/utils';
import { XCircle } from 'lucide-react';
import { getSessionPatientDisplayName } from '../utils/getSessionPatientDisplayName';
import {
	CALENDAR_WEEK_ACTIVE_SESSION_HEIGHT_RATIO,
	CALENDAR_WEEK_CANCELLED_CARD_HEIGHT,
	CALENDAR_WEEK_OVERFLOW_CHIP_HEIGHT,
	dayHasActiveSessionAtHour,
	formatLocalDate,
	formatSessionTimeRange,
	getProfessionalCalendarTimezoneDisplay,
	groupSessionsByTimeSlot,
	isCancelledSession,
	sortSessionsForCalendarDisplay,
	splitCalendarSlotSessions,
} from '../utils/calendarLogic';
import {
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { useProfessionalCalendar } from '@/context/ProfessionalCalendarContext';
import { DaySessionsModal } from './DaySessionsModal';
import { ProfessionalSessionModal } from './ProfessionalSessionModal';
import { useModal } from '@/hooks/useModal';

const STATUS_WEEK_STYLES = {
	CANCELLED: 'bg-red-50 border-red-500 text-red-900',
	FINISHED: 'bg-zinc-100 border-zinc-400 text-zinc-700',
	STARTED: 'bg-orange-50 border-orange-500 text-orange-900',
	CREATED: 'bg-amber-50 border-amber-500 text-amber-900',
	CONFIRMED: 'bg-emerald-50 border-emerald-500 text-emerald-900',
} as Record<Sessions.SessionStatus, string>;

const openWeekSession = (
	session: Sessions.Session,
	setSelectedSession: Dispatch<SetStateAction<Sessions.Session | null>>,
	setIsModalOpen: Dispatch<SetStateAction<boolean>>,
) => {
	setSelectedSession(session);
	setIsModalOpen(true);
};

const WeekCancelledTag = ({
	session,
	className,
	onOpen,
}: {
	session: Sessions.Session;
	className?: string;
	onOpen: () => void;
}) => (
	<button
		type="button"
		onClick={(event) => {
			event.stopPropagation();
			onOpen();
		}}
		className={cn(
			'flex w-full min-w-0 cursor-pointer items-center gap-1 border border-red-200 bg-red-50 px-1.5 text-left transition-colors hover:bg-red-100/80',
			className,
		)}
	>
		<XCircle
			className="size-2.5 shrink-0 text-red-400"
			strokeWidth={2.25}
		/>
		<span className="shrink-0 text-[7px] font-black uppercase tracking-wide text-red-600">
			Cancelado
		</span>
		<span className="min-w-0 truncate text-[8px] font-medium text-red-500/90">
			{formatSessionTimeRange(session.selected_date)}
		</span>
	</button>
);

const WeekActiveSessionCard = ({
	session,
	height,
	className,
	onOpen,
}: {
	session: Sessions.Session;
	height: number | string;
	className?: string;
	onOpen: () => void;
}) => (
	<button
		type="button"
		onClick={onOpen}
		style={{ height }}
		className={cn(
			'flex w-full min-w-0 cursor-pointer flex-col justify-center overflow-hidden border-l-4 p-2 text-left shadow-sm transition-all hover:brightness-95',
			STATUS_WEEK_STYLES[session.status] ||
				'border-blue-500 bg-blue-50 text-blue-900',
			className,
		)}
	>
		<span className="truncate text-[10px] font-bold leading-tight">
			{getSessionPatientDisplayName(session)}
		</span>
		<span className="text-[9px] font-medium opacity-80">
			{formatSessionTimeRange(session.selected_date)}
		</span>
	</button>
);

const WEEK_GRID_COLS = 'grid grid-cols-[60px_repeat(7,minmax(0,1fr))]';
const WEEK_AXIS_TIME_LABEL_CLASS =
	'pointer-events-none z-10 whitespace-nowrap bg-white px-0.5 text-sm font-bold leading-none tabular-nums';
const DESKTOP_HOVER_MEDIA = '(min-width: 1024px)';

const isDesktopHoverEnabled = () =>
	typeof window !== 'undefined' &&
	window.matchMedia(DESKTOP_HOVER_MEDIA).matches;

const isPastWeekSlot = (date: Date, hour: number, reference: Date) => {
	const slotStart = new Date(date);
	slotStart.setHours(hour, 0, 0, 0);
	return slotStart.getTime() < reference.getTime();
};

const timeToGridOffset = (date: Date, hourHeight: number) =>
	(date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600) *
	hourHeight;

const updateWeekSlotHover = (
	setHoveredCell: Dispatch<
		SetStateAction<{ dayIndex: number; hour: number } | null>
	>,
	dayIndex: number,
	hour: number,
	isBookable: boolean,
) => {
	if (!isDesktopHoverEnabled()) return;

	if (isBookable) {
		setHoveredCell({ dayIndex, hour });
		return;
	}

	setHoveredCell(null);
};

export const CalendarWeekView = ({
	days,
	sessions,
	alignNowKey = 0,
	onSlotClick,
}: {
	days: Date[];
	sessions: Sessions.Session[];
	alignNowKey?: number;
	onSlotClick?: (date: Date, hour: number) => void;
}) => {
	const { now } = useProfessionalCalendar();
	const timezoneDisplay = getProfessionalCalendarTimezoneDisplay(now);
	const { openModal, closeModal } = useModal();
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const [selectedSession, setSelectedSession] =
		useState<Sessions.Session | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [hoveredCell, setHoveredCell] = useState<{
		dayIndex: number;
		hour: number;
	} | null>(null);

	const weekDaysLabels = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
	const hours = Array.from({ length: 24 }, (_, i) => i);
	const HOUR_HEIGHT = 65;
	const GRID_BODY_HEIGHT = 24 * HOUR_HEIGHT;
	const ACTIVE_SESSION_HEIGHT =
		CALENDAR_WEEK_ACTIVE_SESSION_HEIGHT_RATIO * HOUR_HEIGHT;
	const CANCELLED_CARD_HEIGHT = CALENDAR_WEEK_CANCELLED_CARD_HEIGHT;
	const OVERFLOW_CHIP_HEIGHT = CALENDAR_WEEK_OVERFLOW_CHIP_HEIGHT;

	const openSlotSessionsModal = (
		slotSessions: Sessions.Session[],
		date: Date,
		openSession: (session: Sessions.Session) => void,
	) => {
		const allSlotSessions = sortSessionsForCalendarDisplay(slotSessions);
		if (allSlotSessions.length === 0) return;

		openModal({
			content: (
				<DaySessionsModal
					date={date}
					sessions={allSlotSessions}
					onSelectSession={(session) => {
						closeModal();
						openSession(session);
					}}
				/>
			),
			title: (
				<span className="text-lg font-bold text-zinc-800">
					Consultas neste horário
				</span>
			),
			size: 'compact',
		});
	};

	const sessionsByDay = sessions.reduce(
		(acc, session) => {
			const dateKey = formatLocalDate(new Date(session.selected_date));
			if (!acc[dateKey]) acc[dateKey] = [];
			acc[dateKey].push(session);
			return acc;
		},
		{} as Record<string, Sessions.Session[]>,
	);

	const nowTop = timeToGridOffset(now, HOUR_HEIGHT);

	const weekKey = days.map((date) => date.toDateString()).join('|');

	const scrollCurrentTimeToCenter = () => {
		const scrollEl = scrollContainerRef.current;
		if (!scrollEl || scrollEl.clientHeight <= 0) return false;

		const today = new Date();
		const todayInWeek = days.some(
			(date) => date.toDateString() === today.toDateString(),
		);
		if (!todayInWeek) return false;

		const currentTop = timeToGridOffset(today, HOUR_HEIGHT);
		const centeredScrollTop = currentTop - scrollEl.clientHeight / 2;
		const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;

		scrollEl.scrollTop = Math.max(
			0,
			Math.min(centeredScrollTop, maxScroll),
		);
		return true;
	};

	useLayoutEffect(() => {
		let frameId = 0;
		let attempts = 0;
		const maxAttempts = 8;

		const tryScroll = () => {
			if (scrollCurrentTimeToCenter() || attempts >= maxAttempts) return;
			attempts += 1;
			frameId = requestAnimationFrame(tryScroll);
		};

		tryScroll();
		return () => cancelAnimationFrame(frameId);
	}, [weekKey, alignNowKey]);

	return (
		<div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
			<div
				ref={scrollContainerRef}
				className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
				onMouseLeave={() => setHoveredCell(null)}
			>
				<div className="w-full min-w-0">
					{/* Cabeçalho — sticky, mesmo grid das colunas do corpo */}
					<div
						className={cn(
							WEEK_GRID_COLS,
							'sticky top-0 z-30 border-b border-zinc-100 bg-white',
						)}
						onMouseEnter={() => {
							if (isDesktopHoverEnabled()) setHoveredCell(null);
						}}
					>
						<div className="flex items-end justify-end border-r border-zinc-100 bg-white px-1 pb-1.5">
							<span
								className={cn(
									WEEK_AXIS_TIME_LABEL_CLASS,
									'text-zinc-400',
								)}
							>
								{timezoneDisplay.abbr}{' '}
								{timezoneDisplay.offsetLabel}
							</span>
						</div>
						{days.map((date, i) => {
							const isToday =
								date.toDateString() ===
								new Date().toDateString();
							const isHoveredDay = hoveredCell?.dayIndex === i;

							return (
								<div
									key={`header-${i}`}
									className="flex flex-col items-center border-r border-zinc-100 py-1.5"
								>
									<span
										className={cn(
											'mb-1 text-[10px] font-black tracking-widest transition-colors',
											isToday
												? 'text-brand-purple'
												: 'text-zinc-400',
										)}
									>
										{weekDaysLabels[i]}
									</span>
									<span
										className={cn(
											'flex h-8 w-8 items-center justify-center rounded-full text-lg font-bold transition-all',
											isToday
												? cn(
														'bg-brand-purple text-white',
														isHoveredDay &&
															'ring-2 ring-brand-purple/30 ring-offset-2',
												  )
												: isHoveredDay
												  ? 'bg-brand-purple/10 text-brand-purple'
												  : 'text-zinc-800',
										)}
									>
										{date.getDate()}
									</span>
								</div>
							);
						})}
					</div>

					{/* Corpo — mesma definição de colunas */}
					<div className={WEEK_GRID_COLS}>
						<div
							className="relative overflow-visible border-r border-zinc-100 bg-white"
							style={{ height: GRID_BODY_HEIGHT }}
							onMouseEnter={() => {
								if (isDesktopHoverEnabled())
									setHoveredCell(null);
							}}
						>
							{hours.map((hour) => {
								const isHoveredHour =
									hoveredCell?.hour === hour;

								return (
									<div
										key={hour}
										style={{ height: HOUR_HEIGHT }}
										className="relative overflow-visible"
									>
										{hour !== 0 && (
											<span
												className={cn(
													WEEK_AXIS_TIME_LABEL_CLASS,
													'absolute right-1 top-0 -translate-y-1/2 transition-colors',
													isHoveredHour
														? 'text-brand-purple'
														: 'text-zinc-400',
												)}
											>
												{`${String(hour).padStart(
													2,
													'0',
												)}:00`}
											</span>
										)}
									</div>
								);
							})}
						</div>

						{days.map((date, i) => {
							const dateKey = formatLocalDate(date);
							const daySessions = sessionsByDay[dateKey] || [];
							const isThisDayToday =
								date.toDateString() === now.toDateString();

							return (
								<div
									key={`day-${i}`}
									className="relative border-r border-zinc-100"
									style={{ height: GRID_BODY_HEIGHT }}
								>
									{hours.map((hour) => {
										const slotStyle = {
											top: hour * HOUR_HEIGHT,
											height: HOUR_HEIGHT,
										};
										const slotOccupied =
											dayHasActiveSessionAtHour(
												daySessions,
												hour,
											);
										const slotIsPast = isPastWeekSlot(
											date,
											hour,
											now,
										);
										const slotIsBookable =
											!!onSlotClick &&
											!slotOccupied &&
											!slotIsPast;

										if (!slotIsBookable) {
											return (
												<div
													key={`slot-${dateKey}-${hour}`}
													style={slotStyle}
													className="absolute inset-x-0 z-[1] border-b border-zinc-100"
													onMouseEnter={() =>
														updateWeekSlotHover(
															setHoveredCell,
															i,
															hour,
															false,
														)
													}
												/>
											);
										}

										return (
											<button
												key={`slot-${dateKey}-${hour}`}
												type="button"
												aria-label={`Agendar em ${dateKey} às ${String(
													hour,
												).padStart(2, '0')}:00`}
												onMouseEnter={() =>
													updateWeekSlotHover(
														setHoveredCell,
														i,
														hour,
														true,
													)
												}
												onClick={() =>
													onSlotClick(date, hour)
												}
												style={slotStyle}
												className="group absolute inset-x-0 z-[1] cursor-pointer border-b border-zinc-100 bg-transparent transition-colors max-lg:hover:bg-transparent lg:hover:bg-brand-purple/5"
											>
												<span className="pointer-events-none hidden h-full w-full items-center justify-center text-[10px] font-bold uppercase tracking-wide text-brand-purple lg:flex lg:opacity-0 lg:transition-opacity lg:group-hover:opacity-100">
													+ Agendar
												</span>
											</button>
										);
									})}

									{isThisDayToday && (
										<div
											className="pointer-events-none absolute left-0 right-0 z-20 flex -translate-y-1/2 items-center"
											style={{ top: nowTop }}
										>
											<div className="-ml-[5px] h-2.5 w-2.5 shrink-0 rounded-full bg-red-500 shadow-sm" />
											<div className="h-px min-h-px flex-1 bg-red-500" />
										</div>
									)}

									{Array.from(
										groupSessionsByTimeSlot(
											daySessions,
										).entries(),
									).flatMap(([, slotSessions]) => {
										const {
											primarySession,
											overflowSessions,
										} =
											splitCalendarSlotSessions(
												slotSessions,
											);
										if (
											!primarySession &&
											overflowSessions.length === 0
										) {
											return [];
										}

										const anchorSession =
											primarySession ??
											overflowSessions[0];
										const topPosition = timeToGridOffset(
											new Date(
												anchorSession.selected_date,
											),
											HOUR_HEIGHT,
										);
										const openSession = (
											session: Sessions.Session,
										) =>
											openWeekSession(
												session,
												setSelectedSession,
												setIsModalOpen,
											);
										const clearHover = () => {
											if (isDesktopHoverEnabled())
												setHoveredCell(null);
										};

										const blocks: ReactNode[] = [];
										const hasOverflow =
											overflowSessions.length > 0;

										if (primarySession && hasOverflow) {
											const activeHeight =
												ACTIVE_SESSION_HEIGHT -
												OVERFLOW_CHIP_HEIGHT;

											blocks.push(
												<div
													key={`slot-mixed-${primarySession.id}`}
													onMouseEnter={clearHover}
													style={{
														top: topPosition,
														height: ACTIVE_SESSION_HEIGHT,
													}}
													className="absolute left-1 right-1 z-10 flex flex-col overflow-hidden rounded-md shadow-sm"
												>
													{isCancelledSession(
														primarySession,
													) ? (
														<WeekCancelledTag
															session={
																primarySession
															}
															className="min-h-0 shrink-0 rounded-b-none"
															onOpen={() =>
																openSession(
																	primarySession,
																)
															}
														/>
													) : (
														<WeekActiveSessionCard
															session={
																primarySession
															}
															height={
																activeHeight
															}
															className="min-h-0 shrink-0 rounded-b-none"
															onOpen={() =>
																openSession(
																	primarySession,
																)
															}
														/>
													)}
													<button
														type="button"
														style={{
															height: OVERFLOW_CHIP_HEIGHT,
														}}
														onClick={(event) => {
															event.stopPropagation();
															openSlotSessionsModal(
																slotSessions,
																date,
																openSession,
															);
														}}
														className="flex shrink-0 items-center justify-center border-t border-zinc-200 bg-zinc-100 text-[9px] font-black text-zinc-500 transition-colors hover:bg-zinc-200"
													>
														+
														{
															overflowSessions.length
														}
													</button>
												</div>,
											);

											return blocks;
										}

										if (
											!primarySession &&
											overflowSessions.length > 1
										) {
											blocks.push(
												<div
													key={`slot-overflow-${anchorSession.id}`}
													onMouseEnter={clearHover}
													style={{
														top: topPosition,
														height: OVERFLOW_CHIP_HEIGHT,
													}}
													className="absolute left-1 right-1 z-10"
												>
													<button
														type="button"
														onClick={(event) => {
															event.stopPropagation();
															openSlotSessionsModal(
																slotSessions,
																date,
																openSession,
															);
														}}
														className="flex h-full w-full items-center justify-center rounded-md bg-zinc-100 text-[9px] font-black text-zinc-500 transition-colors hover:bg-zinc-200"
													>
														+
														{
															overflowSessions.length
														}
													</button>
												</div>,
											);

											return blocks;
										}

										if (
											primarySession &&
											isCancelledSession(primarySession)
										) {
											blocks.push(
												<div
													key={primarySession.id}
													onMouseEnter={clearHover}
													style={{
														top: topPosition,
														height: CANCELLED_CARD_HEIGHT,
													}}
													className="absolute left-1 right-1 z-10"
												>
													<WeekCancelledTag
														session={primarySession}
														className="h-full rounded-md"
														onOpen={() =>
															openSession(
																primarySession,
															)
														}
													/>
												</div>,
											);

											return blocks;
										}

										if (primarySession) {
											blocks.push(
												<div
													key={primarySession.id}
													onMouseEnter={clearHover}
													style={{
														top: topPosition,
														height: ACTIVE_SESSION_HEIGHT,
													}}
													className="absolute left-1 right-1 z-10 overflow-hidden rounded-md"
												>
													<WeekActiveSessionCard
														session={primarySession}
														height="100%"
														className="h-full rounded-md"
														onOpen={() =>
															openSession(
																primarySession,
															)
														}
													/>
												</div>,
											);
										}

										return blocks;
									})}
								</div>
							);
						})}
					</div>
				</div>
			</div>

			<ProfessionalSessionModal
				session={selectedSession}
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
			/>
		</div>
	);
};
